import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import emitter from "./eventBus";
// Keeps Chrome's install prompt from the first page load (see "Instalar o app")
import "./utils/appInstall";
import { installChunkReload } from "./utils/chunkReload";
installChunkReload();

import Helpers from "./helpers";
import "./filters";

//vue-select
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

//@casl/vue
import { abilitiesPlugin } from "@casl/vue";
import { Ability, subject as caslSubject } from "@casl/ability";

//HTTP clients
import axios from "@/axios/index";
import Fetch from "@/fetch/index";

//Rollbar
import Rollbar from "rollbar";
const rollbar = new Rollbar({
  accessToken: "4ccc2ac5aad54cedbf321f2bed36d8a7",
  captureUncaught: true,
  captureUnhandledRejections: true,
  environment: process.env.VUE_APP_ENV,
});

const app = createApp(App);

// Create ability instance
const ability = new Ability([], {});

// Global error handler
app.config.errorHandler = (err, vm, info) => {
  rollbar.error(err);
  throw err; // rethrow
};

// Use plugins
app.use(router);
app.use(store);
app.use(abilitiesPlugin, ability);

// Register global components
app.component("v-select", vSelect);

// Lodash
import lodash from "@/utils/lodash";

// Moment
import moment from "moment";
import "moment/locale/pt-br";
moment.locale("pt-BR");

// Global properties
app.config.globalProperties._ = lodash;
app.config.globalProperties.$moment = moment;
app.config.globalProperties.$ability = ability;
app.config.globalProperties.$can = ability.can.bind(ability);
app.config.globalProperties.$cannot = ability.cannot.bind(ability);
app.config.globalProperties.$caslSubject = caslSubject;
app.config.globalProperties.$eventbus = emitter;
app.config.globalProperties.$fetch = Fetch;
app.config.globalProperties.$helpers = Helpers;
app.config.globalProperties.$rollbar = rollbar;
app.config.globalProperties.axios = axios;

// Palavra que a conta usa para quem atende ($patient/$patients em minúsculas, $Patient/$Patients capitalizadas)
const customerWord = (getter, fallback, lower) => ({
  get() {
    const w = String(store.getters[getter] || fallback).trim() || fallback;
    return lower ? w.toLowerCase() : w;
  },
});
Object.defineProperties(app.config.globalProperties, {
  $patient: customerWord("Companies/customerTreatmentSingular", "paciente", true),
  $patients: customerWord("Companies/customerTreatmentPlural", "pacientes", true),
  $Patient: customerWord("Companies/customerTreatmentSingular", "Paciente", false),
  $Patients: customerWord("Companies/customerTreatmentPlural", "Pacientes", false),
});
app.config.globalProperties.$axios = axios;

// Vue3-mq (media queries)
import { Vue3Mq } from "vue3-mq";
app.use(Vue3Mq, {
  preset: "bootstrap5",
  breakpoints: {
    xs: 0,
    sm: 768,
    md: 1200,
    lg: Infinity,
  },
  defaultBreakpoint: "lg", // Default for SSR or initial load
  global: true, // Enable global $mq property for Options API
});

// Analytics (GA4 + Meta Pixel; no-ops when IDs are not configured)
import { initAnalytics, trackPageView, captureAttribution } from "@/utils/analytics";
import { safeHtmlDirective } from "@/utils/safeHtml";
captureAttribution();
initAnalytics();
router.afterEach((to) => trackPageView(to.fullPath, to.matched.length ? to.matched[to.matched.length - 1].path : null));

// Click outside directive
// Attached on the next tick: the click that opened the element (dropdowns,
// menus) must not be seen as an outside click that closes it right away.
const clickOutsideDirective = {
  mounted(el, binding) {
    el.clickOutsideEvent = function (event) {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    el.clickOutsideTimer = setTimeout(() => document.addEventListener("click", el.clickOutsideEvent), 0);
  },
  unmounted(el) {
    clearTimeout(el.clickOutsideTimer);
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};
app.directive("click-outside", clickOutsideDirective);
app.directive("safe-html", safeHtmlDirective);

// Set fetch vm and store references
Fetch.vm = app;
Fetch.store = store;

app.mount("#app");
